import { FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useAuth from "@/hooks/useAuth";
import calendarsApi from "@/api/calendarsApi";
import ApiError from "@/model/ApiError";
import CalendarFeedResponse from "@/model/CalendarFeedResponse";
import "@/components/CalendarSyncPanel/CalendarSyncPanel.scss";

interface CalendarSyncPanelProps {
  propertyId: string;
}

/**
 * Two-way iCal sync with the channel manager (iGMS): the link iGMS reads our bookings from, and the
 * calendars we import its Airbnb / Booking.com stays from.
 */
function CalendarSyncPanel({ propertyId }: CalendarSyncPanelProps) {
  const { t, i18n } = useTranslation();
  const { session } = useAuth();

  const [exportUrl, setExportUrl] = useState<string | null>(null);
  const [feeds, setFeeds] = useState<CalendarFeedResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const [copied, setCopied] = useState(false);
  const [isRotating, setIsRotating] = useState(false);
  const [busyFeedId, setBusyFeedId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const [name, setName] = useState("iGMS");
  const [url, setUrl] = useState("");
  const [isAdding, setIsAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  useEffect(() => {
    if (!session) {
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setLoadError(false);

    Promise.all([
      calendarsApi.getExport(session.accessToken, propertyId),
      calendarsApi.listFeeds(session.accessToken, propertyId),
    ])
      .then(([exportResponse, feedList]) => {
        if (!cancelled) {
          setExportUrl(exportResponse.url);
          setFeeds(feedList);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLoadError(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [session, propertyId]);

  if (!session) {
    return null;
  }

  const formatTime = (value?: string) =>
    value ? new Date(value).toLocaleString(i18n.language) : "";

  const replaceFeed = (updated: CalendarFeedResponse) =>
    setFeeds((current) =>
      current.map((feed) => (feed.id === updated.id ? updated : feed))
    );

  const handleCopy = async () => {
    if (!exportUrl) {
      return;
    }
    try {
      await navigator.clipboard.writeText(exportUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setActionError(t("calendarSync.export.copyError"));
    }
  };

  const handleRotate = async () => {
    if (!window.confirm(t("calendarSync.export.rotateConfirm"))) {
      return;
    }
    setIsRotating(true);
    setActionError(null);
    try {
      const rotated = await calendarsApi.rotateExport(session.accessToken, propertyId);
      setExportUrl(rotated.url);
    } catch {
      setActionError(t("calendarSync.genericError"));
    } finally {
      setIsRotating(false);
    }
  };

  const handleSync = async (feed: CalendarFeedResponse) => {
    setBusyFeedId(feed.id);
    setActionError(null);
    try {
      replaceFeed(await calendarsApi.syncFeed(session.accessToken, propertyId, feed.id));
    } catch {
      setActionError(t("calendarSync.genericError"));
    } finally {
      setBusyFeedId(null);
    }
  };

  const handleDelete = async (feed: CalendarFeedResponse) => {
    if (!window.confirm(t("calendarSync.feeds.deleteConfirm", { name: feed.name }))) {
      return;
    }
    setBusyFeedId(feed.id);
    setActionError(null);
    try {
      await calendarsApi.deleteFeed(session.accessToken, propertyId, feed.id);
      setFeeds((current) => current.filter((item) => item.id !== feed.id));
    } catch {
      setActionError(t("calendarSync.genericError"));
    } finally {
      setBusyFeedId(null);
    }
  };

  const handleAdd = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsAdding(true);
    setAddError(null);
    try {
      const created = await calendarsApi.createFeed(session.accessToken, propertyId, {
        name: name.trim(),
        url: url.trim(),
      });
      setFeeds((current) => [...current, created]);
      setUrl("");
    } catch (err) {
      setAddError(
        err instanceof ApiError && err.status === 400 && err.message
          ? err.message
          : t("calendarSync.genericError")
      );
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <section className="calendar-sync-panel">
      <div>
        <h2>{t("calendarSync.title")}</h2>
        <p className="calendar-sync-panel__muted">{t("calendarSync.intro")}</p>
      </div>

      {isLoading && <p>{t("calendarSync.loading")}</p>}
      {!isLoading && loadError && <p role="alert">{t("calendarSync.loadError")}</p>}

      {!isLoading && !loadError && (
        <>
          {actionError && (
            <p className="calendar-sync-panel__error" role="alert">
              {actionError}
            </p>
          )}

          <div className="calendar-sync-panel__block">
            <h3>{t("calendarSync.export.title")}</h3>
            <p className="calendar-sync-panel__muted">{t("calendarSync.export.help")}</p>
            <div className="calendar-sync-panel__export">
              <input
                type="text"
                readOnly
                value={exportUrl ?? ""}
                aria-label={t("calendarSync.export.title")}
                onFocus={(event) => event.target.select()}
              />
              <button type="button" onClick={handleCopy}>
                {copied ? t("calendarSync.export.copied") : t("calendarSync.export.copy")}
              </button>
              <button
                type="button"
                className="calendar-sync-panel__secondary"
                onClick={handleRotate}
                disabled={isRotating}
              >
                {t("calendarSync.export.rotate")}
              </button>
            </div>
          </div>

          <div className="calendar-sync-panel__block">
            <h3>{t("calendarSync.feeds.title")}</h3>
            <p className="calendar-sync-panel__muted">{t("calendarSync.feeds.help")}</p>

            {feeds.length === 0 ? (
              <p className="calendar-sync-panel__muted">{t("calendarSync.feeds.empty")}</p>
            ) : (
              <ul className="calendar-sync-panel__feeds">
                {feeds.map((feed) => (
                  <li key={feed.id}>
                    <div className="calendar-sync-panel__feed-info">
                      <strong>{feed.name}</strong>
                      <span className="calendar-sync-panel__url">{feed.url}</span>
                      {feed.lastError ? (
                        <span className="calendar-sync-panel__status calendar-sync-panel__status--error">
                          {t("calendarSync.feeds.failed", {
                            time: formatTime(feed.lastSyncedAt),
                            error: feed.lastError,
                          })}
                        </span>
                      ) : (
                        <span className="calendar-sync-panel__status">
                          {feed.lastSuccessAt
                            ? t("calendarSync.feeds.synced", { time: formatTime(feed.lastSuccessAt) })
                            : t("calendarSync.feeds.neverSynced")}
                        </span>
                      )}
                    </div>
                    <div className="calendar-sync-panel__feed-actions">
                      <button
                        type="button"
                        className="calendar-sync-panel__secondary"
                        onClick={() => handleSync(feed)}
                        disabled={busyFeedId === feed.id}
                      >
                        {busyFeedId === feed.id
                          ? t("calendarSync.feeds.syncing")
                          : t("calendarSync.feeds.syncNow")}
                      </button>
                      <button
                        type="button"
                        className="calendar-sync-panel__danger"
                        onClick={() => handleDelete(feed)}
                        disabled={busyFeedId === feed.id}
                      >
                        {t("calendarSync.feeds.delete")}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}

            <form className="calendar-sync-panel__form" onSubmit={handleAdd}>
              <div>
                <label htmlFor="calendar-feed-name">{t("calendarSync.feeds.nameLabel")}</label>
                <input
                  id="calendar-feed-name"
                  type="text"
                  maxLength={60}
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  required
                />
              </div>
              <div className="calendar-sync-panel__url-field">
                <label htmlFor="calendar-feed-url">{t("calendarSync.feeds.urlLabel")}</label>
                <input
                  id="calendar-feed-url"
                  type="url"
                  placeholder="https://"
                  value={url}
                  onChange={(event) => setUrl(event.target.value)}
                  required
                />
              </div>
              <button type="submit" disabled={isAdding}>
                {isAdding ? t("calendarSync.feeds.adding") : t("calendarSync.feeds.add")}
              </button>
            </form>
            {addError && (
              <p className="calendar-sync-panel__error" role="alert">
                {addError}
              </p>
            )}
          </div>
        </>
      )}
    </section>
  );
}

export default CalendarSyncPanel;
export type { CalendarSyncPanelProps };
